import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit } from "@angular/core";
import { PermitStep, PermitType, Translation } from "@ha/data/basic";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Navigate } from "@ngxs/router-plugin";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { combineLatest, Observable, Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";

import { FormControl, FormGroup, Validators } from "@angular/forms";
import { HaModalService } from "@ha/ui/common";
import { checkBoxValidator } from "@ha/util/common";
import { PermitPaths } from "../../../../permit-paths";
import { CurrentStep } from "../../../../permit/models/current-step.model";
import { PermitStepSelectors } from "../../../../permit/states/permit-step.selectors";
import { WheelDatetimePickerModalComponent } from "../../../../shared/components/wheel-datetime-picker/wheel-datetime-picker-modal.component";
import { CustomerPermitTemplate, FlammableMaterial } from "../../../models/customer-permit-template.model";
import { QuestionAnswerEnum } from "../../../models/fixed-template.model";
import { Permit } from "../../../models/permit.model";
import { CustomerQuestion, CustomerQuestionAnswer, FixedQuestion, FlammableMaterialMeasurement, QuestionAnswer, QuestionAnswerType, QuestionOption, SafetyEquipmentAnswerSelection, SafetyEquipmentOption } from "../../../models/question.model";
import { PermitCreateActions } from "../../../states/permit-create.actions";
import { PermitCreateSelectors } from "../../../states/permit-create.selectors";
import { PermitQuestionActions } from "../../../states/permit-question.actions";
import { PermitQuestionSelectors } from "../../../states/permit-question.selectors";
import { FlammableMaterialSearchModalComponent } from "../flammable-material-search-modal/flammable-material-search-modal.component";

type FlammableAtexPermitActionState = "none" | "loading" | "superintendent" | "nonSuperintendent";

interface OptionSelectionAnswerValue {
    selectedOptionIds: number[];
    freeText?: string | null;
}

@UntilDestroy()
@Component({
    selector: "pmt-question",
    templateUrl: "permit-question.component.html",
    styleUrls: ["permit-question.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitQuestionComponent implements OnChanges, OnInit {
    private static readonly flammableAtexPermitTriggerQuestionId = 1;
    private static readonly missingCommentTranslationText = "Translation not available.";
    private static readonly defaultNewMaterialsMaxConcentrationFactor = 0.2;
    private currentQuestionKey: string | null = null;
    private validDateTimeEmissionQuestionKey: string | null = null;
    private hasProcessedValidDateTimeEmissionForQuestion = false;
    private readonly cancelFlammableMaterialsLoad$ = new Subject<void>();

    @Input()
    public fixedQuestion: FixedQuestion | null;
    @Input()
    public customerQuestion: CustomerQuestion | null;

    @Select(PermitStepSelectors.currentStep)
    public currentStep$!: Observable<CurrentStep>;

    @Select(PermitCreateSelectors.companyName)
    public companyName$: Observable<string>;

    @Select(PermitCreateSelectors.permit)
    public permit$: Observable<Permit>;

    @Select(PermitCreateSelectors.customerPermitTemplate)
    public customerPermitTemplate$: Observable<CustomerPermitTemplate>;

    @Select(PermitCreateSelectors.allFlammableMaterials)
    public allFlammableMaterials$: Observable<FlammableMaterial[]>;

    @Select(PermitCreateSelectors.validDateTime)
    public validDateTime$: Observable<string>;
    public validDateTime: string;

    public PermitType = PermitType;

    public QuestionAnswerType = QuestionAnswerType;

    public PermitStep = PermitStep;
    public CustomerQuestionAnswer = CustomerQuestionAnswer;

    public currentCustomerAnswer: CustomerQuestionAnswer;
    public alertConfirmed = false;

    public form: FormGroup;
    public loadingFlammableMaterials = false;
    public flammableMaterials: FlammableMaterial[] = [];
    public selectedMaterialIds: Set<number> = new Set();
    public materialsWithMeasurements: FlammableMaterial[] = [];
    public substanceMeasurements: FlammableMaterialMeasurement[] = [];
    public substanceMeasurementsValid = false;
    public requiresManualFlammableMaterialAddition = false;
    public selectedQuestionOptionIds: Set<number> = new Set();
    public selectedSafetyEquipmentIds: Set<number> = new Set();
    public customSafetyEquipmentSelected = false;

    public get canProceed() {
        if (this.fixedQuestion) {
            switch (this.fixedQuestion.answerType) {
                case QuestionAnswerType.Text:
                case QuestionAnswerType.Number:
                    return this.form?.valid ?? false;

                case QuestionAnswerType.SubstanceSelection:
                    return this.form?.valid ?? false;

                case QuestionAnswerType.OptionSelection:
                    if (this.selectedQuestionOptionIds.size <= 0) {
                        return false;
                    }

                    if (this.showOptionSelectionFreeTextInput && this.fixedQuestion.answerTypeConfiguration?.freeTextRequired) {
                        return this.form?.valid ?? false;
                    }

                    return true;

                case QuestionAnswerType.SafetyEquipmentSelection:
                    return this.hasSelectedSafetyEquipment
                        && (!this.customSafetyEquipmentSelected || this.form?.valid === true);

                case QuestionAnswerType.SubstanceMeasurement:
                    return this.substanceMeasurementsValid;

                case QuestionAnswerType.YesNoWithCondition:
                    if (!this.currentCustomerAnswer) {
                        return false;
                    }
                    if (this.showConfirmDialog && !this.alertConfirmed) {
                        return false;
                    }
                    if (this.shouldShowConditionInput) {
                        return this.form?.valid ?? false;
                    }
                    return true;

                case QuestionAnswerType.DateTime:
                    if (!this.currentCustomerAnswer) {
                        return false;
                    }
                    if (this.showConfirmDialog && !this.alertConfirmed) {
                        return false;
                    }
                    if (this.currentCustomerAnswer === CustomerQuestionAnswer.SpecificTime) {
                        return this.form?.valid ?? false;
                    }
                    return true;

                case QuestionAnswerType.YesNo:
                case QuestionAnswerType.YesNoNotApplicable:
                    if (!this.currentCustomerAnswer) {
                        return false;
                    }
                    if (this.showConfirmDialog && !this.alertConfirmed) {
                        return false;
                    }
                    return true;
            }
        }

        if (this.customerQuestion) {
            if (!this.currentCustomerAnswer) {
                return false;
            }
            return true;
        }

        return false;
    }

    public get showConditionOnYes(): boolean {
        return this.fixedQuestion?.answerType === QuestionAnswerType.YesNoWithCondition
            && this.fixedQuestion.answerTypeConfiguration?.showConditionOnYes === true;
    }

    public get shouldShowConditionInput(): boolean {
        if (this.fixedQuestion?.answerType !== QuestionAnswerType.YesNoWithCondition) {
            return false;
        }

        if (this.showConditionOnYes) {
            return this.currentCustomerAnswer === CustomerQuestionAnswer.Yes;
        }

        return this.currentCustomerAnswer === CustomerQuestionAnswer.Condition;
    }

    public get showConfirmDialog(): boolean {
        if (this.fixedQuestion?.alert?.answerIsYes && this.currentCustomerAnswer === CustomerQuestionAnswer.Yes) {
            return true;
        } else if (this.fixedQuestion?.alert) {
            if (
                this.fixedQuestion.alert.answerIsNo
                && this.currentCustomerAnswer === CustomerQuestionAnswer.No
                && !this.fixedQuestion.alert.confirmToProceed
            ) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    public get showCreateFlammableAtexPermitButton(): boolean {
        return this.flammableAtexPermitActionState === "superintendent";
    }

    public get showRequestFlammableAtexPermitButton(): boolean {
        return this.flammableAtexPermitActionState === "nonSuperintendent"
            && this.store.selectSnapshot(PermitCreateSelectors.hasRetrievableFlammableAtexPermit) !== true;
    }

    public get showRetrieveFlammableAtexPermitButton(): boolean {
        return this.flammableAtexPermitActionState === "nonSuperintendent"
            && this.store.selectSnapshot(PermitCreateSelectors.hasRetrievableFlammableAtexPermit) === true;
    }

    public get showInlineFlammableAtexActions(): boolean {
        return this.showCreateFlammableAtexPermitButton
            || this.showRetrieveFlammableAtexPermitButton
            || this.showRequestFlammableAtexPermitButton;
    }

    public get disableDefaultContinueButton(): boolean {
        return this.isFlammableAtexPermitActionLoading || this.showInlineFlammableAtexActions || !this.canProceed;
    }

    public get isFlammableAtexPermitActionLoading(): boolean {
        return this.flammableAtexPermitActionState === "loading";
    }

    private get flammableAtexPermitActionState(): FlammableAtexPermitActionState {
        if (!this.shouldEvaluateFlammableAtexPermitAction) {
            return "none";
        }

        const isCurrentUserSuperintendent = this.store.selectSnapshot(
            PermitCreateSelectors.isCurrentUserSuperintendent,
        );
        if (isCurrentUserSuperintendent === undefined) {
            return "loading";
        }

        return isCurrentUserSuperintendent === true ? "superintendent" : "nonSuperintendent";
    }

    private get shouldEvaluateFlammableAtexPermitAction(): boolean {
        if (!this.fixedQuestion) {
            return false;
        }

        if (this.currentCustomerAnswer !== CustomerQuestionAnswer.Yes) {
            return false;
        }

        if (this.fixedQuestion.id !== PermitQuestionComponent.flammableAtexPermitTriggerQuestionId) {
            return false;
        }

        const permit = this.store.selectSnapshot(PermitCreateSelectors.permit);
        return permit?.type === PermitType.hotWork
            && permit?.isPremium === true
            && permit?.workplaceId != null
            && permit.workplaceId > 0
            && this.store.selectSnapshot(PermitCreateSelectors.hasFlammableAtexAddOn) === true
            && permit?.flammableAtexPermitId == null;
    }

    constructor(
        private store: Store,
        private actions$: Actions,
        private modalService: HaModalService,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.validDateTime$.pipe(untilDestroyed(this)).subscribe((dateTime) => {
            if (this.form?.get("dateTime")) {
                if (!this.shouldApplyValidDateTimeEmission()) {
                    return;
                }

                this.validDateTime = dateTime;
                this.form.get("dateTime").setValue(dateTime);
                this.cdr.detectChanges();
            }
        });

        combineLatest([
            this.store.select(PermitCreateSelectors.isCurrentUserSuperintendent),
            this.store.select(PermitCreateSelectors.hasFlammableAtexAddOn),
            this.store.select(PermitCreateSelectors.hasRetrievableFlammableAtexPermit),
        ]).pipe(untilDestroyed(this)).subscribe(() => {
            this.cdr.markForCheck();
        });
    }

    private loadFlammableMaterials(): void {
        this.loadingFlammableMaterials = true;
        this.cancelFlammableMaterialsLoad$.next();

        const permit = this.store.selectSnapshot(PermitCreateSelectors.permit);

        if (permit?.customerPermitTemplateId) {
            this.store.dispatch(
                new PermitCreateActions.GetCustomerPermitTemplate(
                    permit.customerPermitTemplateId,
                    permit.subscriptionId,
                ),
            );
        }

        this.store.dispatch(new PermitCreateActions.GetAllFlammableMaterials());

        combineLatest([this.permit$, this.customerPermitTemplate$, this.allFlammableMaterials$]).pipe(
            filter(([currentPermit, template, allFlammableMaterials]) => {
                if (!currentPermit || !allFlammableMaterials) {
                    return false;
                }

                return !currentPermit.customerPermitTemplateId || !!template;
            }),
            takeUntil(this.cancelFlammableMaterialsLoad$),
            take(1),
            untilDestroyed(this),
        ).subscribe(([permit, template, allFlammableMaterials]) => {
            const hasTemplateId = !!permit.customerPermitTemplateId;

            this.requiresManualFlammableMaterialAddition = !hasTemplateId;

            const templateFlammableMaterials = template?.flammableMaterials.map((material) => {
                const flammableMaterial = allFlammableMaterials.find((f) =>
                    (f.flammableMaterialId ?? f.id) === material.flammableMaterialId
                );

                return {
                    id: material.flammableMaterialId,
                    flammableMaterialId: material.flammableMaterialId,
                    maxConcentration: material.maxConcentration,
                    name: flammableMaterial?.name,
                    cas: flammableMaterial?.cas,
                    unNumber: flammableMaterial?.unNumber,
                    lel: flammableMaterial?.lel,
                    uel: flammableMaterial?.uel,
                    flashpoint: flammableMaterial?.flashpoint,
                    boilingPoint: flammableMaterial?.boilingPoint,
                    hazardNumber: flammableMaterial?.hazardNumber,
                    class: flammableMaterial?.class,
                    packingGroup: flammableMaterial?.packingGroup,
                    canDelete: flammableMaterial?.canDelete,
                    matchedSynonym: flammableMaterial?.matchedSynonym,
                };
            }) ?? [];

            const templateFlammableMaterialIds = new Set(
                templateFlammableMaterials.map((material) => material.flammableMaterialId),
            );
            const shouldRestoreSavedSelection = this.shouldRestoreSavedSubstanceSelection();

            this.flammableMaterials = [
                ...templateFlammableMaterials,
                ...(shouldRestoreSavedSelection
                    ? this.getExtraSavedFlammableMaterials(
                        templateFlammableMaterialIds,
                        template,
                        allFlammableMaterials,
                    )
                    : []),
            ];

            if (this.fixedQuestion?.answerType === QuestionAnswerType.SubstanceMeasurement) {
                this.updateMaterialsWithMeasurements(template, allFlammableMaterials);
            }

            this.initializeForm();
            this.populateFormFromCurrentAnswer();
            this.loadingFlammableMaterials = false;
            this.cdr.detectChanges();
        });
    }

    private initializeForm(): void {
        if (this.fixedQuestion) {
            switch (this.fixedQuestion.answerType) {
                case QuestionAnswerType.SubstanceSelection:
                    this.form = new FormGroup({}, { validators: [checkBoxValidator()] });
                    this.flammableMaterials.forEach((material) => {
                        this.form.addControl(`material-${material.flammableMaterialId}`, new FormControl(false));
                    });
                    break;
                case QuestionAnswerType.OptionSelection:
                    this.form = new FormGroup({
                        optionSelectionFreeText: new FormControl(
                            "",
                            [this.fixedQuestion.answerTypeConfiguration?.freeTextRequired ? Validators.required : null]
                                .filter((v) => v !== null),
                        ),
                    });
                    break;
                case QuestionAnswerType.SafetyEquipmentSelection:
                    this.form = new FormGroup({
                        safetyEquipmentCustomText: new FormControl("", [Validators.required]),
                    });
                    break;
                case QuestionAnswerType.SubstanceMeasurement:
                    // Form is owned by the pmt-substance-measurement child component
                    break;
                case QuestionAnswerType.YesNoWithCondition:
                    this.form = new FormGroup({
                        condition: new FormControl(
                            "",
                            [this.fixedQuestion.answerTypeConfiguration?.conditionRequired ? Validators.required : null]
                                .filter((v) => v !== null),
                        ),
                    });
                    break;
                case QuestionAnswerType.Text:
                    this.form = new FormGroup({
                        textNumber: new FormControl(
                            "",
                            [
                                this.fixedQuestion.answerTypeConfiguration?.required !== false ? Validators.required : null,
                                this.fixedQuestion.answerTypeConfiguration?.maxLength
                                    ? Validators.maxLength(this.fixedQuestion.answerTypeConfiguration?.maxLength)
                                    : null,
                                this.fixedQuestion.answerTypeConfiguration?.minLength
                                    ? Validators.minLength(this.fixedQuestion.answerTypeConfiguration?.minLength)
                                    : null,
                            ].filter((v) => v !== null),
                        ),
                    });
                    break;
                case QuestionAnswerType.Number:
                    this.form = new FormGroup({
                        textNumber: new FormControl(
                            0,
                            [
                                Validators.required,
                                this.fixedQuestion.answerTypeConfiguration?.minValue
                                    ? Validators.min(this.fixedQuestion.answerTypeConfiguration?.minValue)
                                    : null,
                                this.fixedQuestion.answerTypeConfiguration?.maxValue
                                    ? Validators.max(this.fixedQuestion.answerTypeConfiguration?.maxValue)
                                    : null,
                            ].filter((v) => v !== null),
                        ),
                    });
                    break;
                case QuestionAnswerType.DateTime:
                    this.form = new FormGroup({
                        dateTime: new FormControl("", [Validators.required]),
                    });
                    break;
            }
        }
    }

    public openAddNewFlammableMaterialDialog(): void {
        if (!this.form) {
            return;
        }

        const permit = this.store.selectSnapshot(PermitCreateSelectors.permit);
        const existingMaterialIds = this.flammableMaterials.map((material) => material.flammableMaterialId);

        this.modalService.open(FlammableMaterialSearchModalComponent, true, {
            subscriptionId: permit.subscriptionId,
            existingMaterialIds: existingMaterialIds,
            onSelect: (material: FlammableMaterial) => {
                this.upsertAndSelectFlammableMaterial(material);
            },
        });
    }

    private upsertAndSelectFlammableMaterial(material: FlammableMaterial): void {
        if (!this.form) {
            return;
        }

        const materialId = material.flammableMaterialId ?? material.id;
        const controlName = `material-${materialId}`;
        const normalizedMaterial: FlammableMaterial = {
            ...material,
            id: material.id ?? materialId,
            flammableMaterialId: materialId,
        };

        const existingMaterial = this.flammableMaterials.find((m) => m.flammableMaterialId === materialId);
        if (existingMaterial) {
            this.flammableMaterials = this.flammableMaterials.map((currentMaterial) =>
                currentMaterial.flammableMaterialId === materialId
                    ? {
                        ...currentMaterial,
                        ...normalizedMaterial,
                    }
                    : currentMaterial
            );
        } else {
            this.flammableMaterials = [...this.flammableMaterials, normalizedMaterial];
        }

        if (!this.form.get(controlName)) {
            this.form.addControl(controlName, new FormControl(false));
        }

        this.form.get(controlName)?.setValue(true);
        this.selectedMaterialIds.add(materialId);
        this.cdr.detectChanges();
    }

    private preselectAllFlammableMaterials(): void {
        if (!this.form) {
            return;
        }

        this.flammableMaterials.forEach((material) => {
            const materialId = material.flammableMaterialId;
            this.selectedMaterialIds.add(materialId);
            this.form.get(`material-${materialId}`)?.setValue(true);
        });
    }

    public ngOnChanges(): void {
        const previousQuestionKey = this.currentQuestionKey;
        const previousAlertConfirmed = this.alertConfirmed;
        const nextQuestionKey = this.getQuestionKey();
        const savedAnswer = this.fixedQuestion
            ? this.getSavedFixedQuestionAnswer(this.fixedQuestion)
            : this.getSavedCustomerQuestionAnswer(this.customerQuestion);

        this.alertConfirmed = false;
        this.currentCustomerAnswer = undefined;
        this.selectedMaterialIds.clear();
        this.selectedQuestionOptionIds.clear();
        this.selectedSafetyEquipmentIds.clear();
        this.customSafetyEquipmentSelected = false;

        if (this.fixedQuestion) {
            if (
                this.fixedQuestion.answerType === QuestionAnswerType.SubstanceSelection
                || this.fixedQuestion.answerType === QuestionAnswerType.SubstanceMeasurement
            ) {
                // Clear form and materials arrays to prevent template from rendering with stale data
                this.form = null;
                this.flammableMaterials = [];
                this.materialsWithMeasurements = [];
                this.loadFlammableMaterials();
            } else {
                if (this.form) {
                    this.form.reset();
                }
                this.initializeForm();
                this.populateFormFromCurrentAnswer();
                if (savedAnswer !== undefined) {
                    this.select(savedAnswer);
                }
            }
        } else if (this.customerQuestion) {
            if (savedAnswer !== undefined) {
                this.select(savedAnswer);
            }
        }

        const shouldPreserveAlertConfirmation = previousAlertConfirmed === true
            && previousQuestionKey === nextQuestionKey
            && this.showConfirmDialog;

        if (shouldPreserveAlertConfirmation) {
            this.alertConfirmed = true;
        }

        this.currentQuestionKey = nextQuestionKey;
    }

    private populateFormFromCurrentAnswer(): void {
        if (!this.fixedQuestion || !this.form) {
            return;
        }

        switch (this.fixedQuestion.answerType) {
            case QuestionAnswerType.SubstanceSelection:
                {
                    const hasSavedMeasurements = this.shouldRestoreSavedSubstanceSelection()
                        && this.fixedQuestion.currentFlammableMaterialMeasurements
                        && this.fixedQuestion.currentFlammableMaterialMeasurements.length > 0;

                    if (hasSavedMeasurements) {
                        this.fixedQuestion.currentFlammableMaterialMeasurements.forEach((measurement) => {
                            this.selectedMaterialIds.add(measurement.flammableMaterialId);
                            const control = this.form.get(`material-${measurement.flammableMaterialId}`);
                            if (control) {
                                control.setValue(true);
                            }
                        });
                    } else if (!this.hasValidCustomerAnswer(this.fixedQuestion.currentAnswer)) {
                        this.preselectAllFlammableMaterials();
                    }
                }
                break;
            case QuestionAnswerType.SubstanceMeasurement:
                this.updateMaterialsWithMeasurements();
                break;
            case QuestionAnswerType.OptionSelection:
                {
                    const selection = this.parseOptionSelectionAnswer(this.fixedQuestion.currentTextAnswer);
                    this.selectedQuestionOptionIds = new Set(selection.selectedOptionIds);
                    this.form.get("optionSelectionFreeText")?.setValue(selection.freeText ?? "");
                }
                break;
            case QuestionAnswerType.SafetyEquipmentSelection:
                if (this.fixedQuestion.currentSafetyEquipmentAnswers?.length > 0) {
                    const customTexts: string[] = [];
                    this.fixedQuestion.currentSafetyEquipmentAnswers.forEach((answer) => {
                        if (answer.safetyEquipmentId != null) {
                            this.selectedSafetyEquipmentIds.add(answer.safetyEquipmentId);
                            return;
                        }

                        if (answer.customText?.trim()) {
                            this.customSafetyEquipmentSelected = true;
                            customTexts.push(answer.customText.trim());
                        }
                    });

                    if (customTexts.length > 0) {
                        this.form.get("safetyEquipmentCustomText")?.setValue(customTexts.join(", "));
                    }
                }
                break;
            case QuestionAnswerType.Text:
                if (
                    this.fixedQuestion.currentTextAnswer !== undefined && this.fixedQuestion.currentTextAnswer !== null
                ) {
                    this.form.get("textNumber")?.setValue(this.fixedQuestion.currentTextAnswer);
                }
                break;
            case QuestionAnswerType.Number:
                if (
                    this.fixedQuestion.currentNumberAnswer !== undefined
                    && this.fixedQuestion.currentNumberAnswer !== null
                ) {
                    this.form.get("textNumber")?.setValue(this.fixedQuestion.currentNumberAnswer);
                }
                break;
            case QuestionAnswerType.YesNoWithCondition:
                if (
                    this.fixedQuestion.currentConditionText !== undefined
                    && this.fixedQuestion.currentConditionText !== null
                ) {
                    this.form.get("condition")?.setValue(this.fixedQuestion.currentConditionText);
                }
                break;
            case QuestionAnswerType.DateTime:
                if (
                    this.fixedQuestion.currentDateTimeAnswer !== undefined
                    && this.fixedQuestion.currentDateTimeAnswer !== null
                ) {
                    const currentDateTimeAnswer = this.toIsoString(this.fixedQuestion.currentDateTimeAnswer);
                    this.validDateTime = currentDateTimeAnswer;
                    this.form.get("dateTime")?.setValue(currentDateTimeAnswer);
                    this.currentCustomerAnswer = CustomerQuestionAnswer.SpecificTime;
                }
                break;
        }
    }

    private getSavedFixedQuestionAnswer(fixedQuestion: FixedQuestion | null | undefined): CustomerQuestionAnswer | undefined {
        if (!fixedQuestion) {
            return undefined;
        }

        if (fixedQuestion.answerType === QuestionAnswerType.DateTime) {
            if (
                fixedQuestion.currentDateTimeAnswer !== undefined
                && fixedQuestion.currentDateTimeAnswer !== null
            ) {
                return CustomerQuestionAnswer.SpecificTime;
            }

            if (
                fixedQuestion.currentAnswer === CustomerQuestionAnswer.Now
                || fixedQuestion.currentAnswer === CustomerQuestionAnswer.SpecificTime
            ) {
                return fixedQuestion.currentAnswer;
            }

            return undefined;
        }

        if (fixedQuestion.answerType === QuestionAnswerType.YesNoWithCondition) {
            if (this.hasValidCustomerAnswer(fixedQuestion.currentAnswer)) {
                return fixedQuestion.currentAnswer;
            }

            if (fixedQuestion.currentConditionText === undefined || fixedQuestion.currentConditionText === null) {
                return undefined;
            }

            return fixedQuestion.answerTypeConfiguration?.showConditionOnYes === true
                ? CustomerQuestionAnswer.Yes
                : CustomerQuestionAnswer.Condition;
        }

        if (this.hasValidCustomerAnswer(fixedQuestion.currentAnswer)) {
            return fixedQuestion.currentAnswer;
        }

        return undefined;
    }

    private getSavedCustomerQuestionAnswer(
        customerQuestion: CustomerQuestion | null | undefined,
    ): CustomerQuestionAnswer | undefined {
        if (!customerQuestion || !this.hasValidCustomerAnswer(customerQuestion.currentAnswer)) {
            return undefined;
        }

        return customerQuestion.currentAnswer;
    }

    private shouldApplyValidDateTimeEmission(): boolean {
        const questionKey = this.getQuestionKey();

        if (this.validDateTimeEmissionQuestionKey !== questionKey) {
            this.validDateTimeEmissionQuestionKey = questionKey;
            this.hasProcessedValidDateTimeEmissionForQuestion = false;
        }

        if (this.hasProcessedValidDateTimeEmissionForQuestion) {
            return true;
        }

        this.hasProcessedValidDateTimeEmissionForQuestion = true;
        return !this.hasSavedCurrentDateTimeAnswer();
    }

    private hasSavedCurrentDateTimeAnswer(): boolean {
        return this.fixedQuestion?.answerType === QuestionAnswerType.DateTime
            && this.fixedQuestion.currentDateTimeAnswer !== undefined
            && this.fixedQuestion.currentDateTimeAnswer !== null;
    }

    private toIsoString(dateTime: Date | string): string {
        if (dateTime instanceof Date) {
            return dateTime.toISOString();
        }

        return dateTime;
    }

    public toggleMaterialSelection(materialId: number): void {
        if (this.selectedMaterialIds.has(materialId)) {
            this.selectedMaterialIds.delete(materialId);
        } else {
            this.selectedMaterialIds.add(materialId);
        }
        const control = this.form.get(`material-${materialId}`);
        if (control) {
            control.setValue(!control.value);
        }
        this.cdr.detectChanges();
    }

    public isMaterialSelected(materialId: number): boolean {
        return this.selectedMaterialIds.has(materialId);
    }

    public get sortedSafetyEquipmentOptions(): SafetyEquipmentOption[] {
        return [...(this.fixedQuestion?.safetyEquipmentOptions ?? [])].sort((left, right) =>
            left.priority - right.priority
        );
    }

    public get hasSelectedSafetyEquipment(): boolean {
        const customText = (this.form?.get("safetyEquipmentCustomText")?.value as string | null)?.trim();
        return this.selectedSafetyEquipmentIds.size > 0 || (this.customSafetyEquipmentSelected && !!customText);
    }

    public get questionOptions(): QuestionOption[] {
        return this.fixedQuestion?.answerTypeConfiguration?.options ?? [];
    }

    public get allowMultipleQuestionOptions(): boolean {
        return this.fixedQuestion?.answerTypeConfiguration?.allowMultiple !== false;
    }

    public get showOptionSelectionFreeTextInput(): boolean {
        const freeTextOptionId = this.fixedQuestion?.answerTypeConfiguration?.freeTextOptionId;
        return freeTextOptionId != null && this.selectedQuestionOptionIds.has(freeTextOptionId);
    }

    public toggleSafetyEquipmentOption(optionId: number): void {
        if (this.selectedSafetyEquipmentIds.has(optionId)) {
            this.selectedSafetyEquipmentIds.delete(optionId);
        } else {
            this.selectedSafetyEquipmentIds.add(optionId);
        }

        this.cdr.detectChanges();
    }

    public isSafetyEquipmentOptionSelected(optionId: number): boolean {
        return this.selectedSafetyEquipmentIds.has(optionId);
    }

    public toggleQuestionOption(optionId: number): void {
        if (this.allowMultipleQuestionOptions) {
            if (this.selectedQuestionOptionIds.has(optionId)) {
                this.selectedQuestionOptionIds.delete(optionId);
            } else {
                this.selectedQuestionOptionIds.add(optionId);
            }
        } else {
            if (this.selectedQuestionOptionIds.has(optionId)) {
                this.selectedQuestionOptionIds.clear();
            } else {
                this.selectedQuestionOptionIds.clear();
                this.selectedQuestionOptionIds.add(optionId);
            }
        }

        const freeTextOptionId = this.fixedQuestion?.answerTypeConfiguration?.freeTextOptionId;
        if (freeTextOptionId != null && !this.selectedQuestionOptionIds.has(freeTextOptionId)) {
            this.form?.get("optionSelectionFreeText")?.setValue("");
        }

        this.cdr.detectChanges();
    }

    public isQuestionOptionSelected(optionId: number): boolean {
        return this.selectedQuestionOptionIds.has(optionId);
    }

    public toggleCustomSafetyEquipment(): void {
        this.customSafetyEquipmentSelected = !this.customSafetyEquipmentSelected;
        if (!this.customSafetyEquipmentSelected) {
            this.form?.get("safetyEquipmentCustomText")?.setValue("");
        }

        this.cdr.detectChanges();
    }

    public onMeasurementsChange(measurements: FlammableMaterialMeasurement[]): void {
        this.substanceMeasurements = measurements;
    }

    public onMeasurementsValidChange(valid: boolean): void {
        this.substanceMeasurementsValid = valid;
    }

    private updateMaterialsWithMeasurements(
        template?: CustomerPermitTemplate,
        allFlammableMaterials?: FlammableMaterial[],
    ): void {
        if (
            !this.fixedQuestion?.currentFlammableMaterialMeasurements
            || this.fixedQuestion.currentFlammableMaterialMeasurements.length === 0
        ) {
            this.materialsWithMeasurements = [];
            return;
        }

        const resolvedTemplate = template ?? this.store.selectSnapshot(PermitCreateSelectors.customerPermitTemplate);
        const newMaterialsMaxConcentrationFactor = resolvedTemplate?.newMaterialsMaxConcentrationFactor
            ?? PermitQuestionComponent.defaultNewMaterialsMaxConcentrationFactor;

        const materialIdsWithMeasurements = new Set(
            this.fixedQuestion.currentFlammableMaterialMeasurements.map((m) => m.flammableMaterialId),
        );

        const materialsFromTemplate = this.flammableMaterials.filter((material) =>
            materialIdsWithMeasurements.has(material.flammableMaterialId)
        );

        const missingMaterialIds = [...materialIdsWithMeasurements].filter((id) =>
            !this.flammableMaterials.some((m) => m.flammableMaterialId === id)
        );

        const resolvedAllMaterials = allFlammableMaterials
            ?? this.store.selectSnapshot(PermitCreateSelectors.allFlammableMaterials)
            ?? [];

        const extraMaterials: FlammableMaterial[] = missingMaterialIds.map((id) => {
            const source = resolvedAllMaterials.find((f) => (f.flammableMaterialId ?? f.id) === id);
            const lel = source?.lel ?? 0;
            const maxConcentration = newMaterialsMaxConcentrationFactor * lel;
            return {
                id: id,
                flammableMaterialId: id,
                maxConcentration: maxConcentration,
                name: source?.name,
                cas: source?.cas,
                unNumber: source?.unNumber,
                lel: source?.lel,
                uel: source?.uel,
                flashpoint: source?.flashpoint,
                boilingPoint: source?.boilingPoint,
                hazardNumber: source?.hazardNumber,
                class: source?.class,
                packingGroup: source?.packingGroup,
                canDelete: source?.canDelete,
                matchedSynonym: source?.matchedSynonym,
            };
        });

        this.materialsWithMeasurements = [...materialsFromTemplate, ...extraMaterials];
    }

    private getExtraSavedFlammableMaterials(
        existingMaterialIds: Set<number>,
        template?: CustomerPermitTemplate,
        allFlammableMaterials?: FlammableMaterial[],
    ): FlammableMaterial[] {
        if (
            !this.fixedQuestion?.currentFlammableMaterialMeasurements
            || this.fixedQuestion.currentFlammableMaterialMeasurements.length === 0
        ) {
            return [];
        }

        const resolvedTemplate = template ?? this.store.selectSnapshot(PermitCreateSelectors.customerPermitTemplate);
        const newMaterialsMaxConcentrationFactor = resolvedTemplate?.newMaterialsMaxConcentrationFactor
            ?? PermitQuestionComponent.defaultNewMaterialsMaxConcentrationFactor;

        const savedMaterialIds = new Set(
            this.fixedQuestion.currentFlammableMaterialMeasurements.map((measurement) => measurement.flammableMaterialId),
        );

        const resolvedAllMaterials = allFlammableMaterials
            ?? this.store.selectSnapshot(PermitCreateSelectors.allFlammableMaterials)
            ?? [];

        return [...savedMaterialIds]
            .filter((materialId) => !existingMaterialIds.has(materialId))
            .map((materialId) => {
                const source = resolvedAllMaterials.find((material) =>
                    (material.flammableMaterialId ?? material.id) === materialId
                );
                const lel = source?.lel ?? 0;

                return {
                    id: materialId,
                    flammableMaterialId: materialId,
                    maxConcentration: newMaterialsMaxConcentrationFactor * lel,
                    name: source?.name,
                    cas: source?.cas,
                    unNumber: source?.unNumber,
                    lel: source?.lel,
                    uel: source?.uel,
                    flashpoint: source?.flashpoint,
                    boilingPoint: source?.boilingPoint,
                    hazardNumber: source?.hazardNumber,
                    class: source?.class,
                    packingGroup: source?.packingGroup,
                    canDelete: source?.canDelete,
                    matchedSynonym: source?.matchedSynonym,
                };
            });
    }

    private shouldRestoreSavedSubstanceSelection(): boolean {
        if (this.fixedQuestion?.answerType !== QuestionAnswerType.SubstanceSelection) {
            return true;
        }

        return this.store.selectSnapshot(PermitQuestionSelectors.navigationSource) !== "next";
    }

    public select(answer: CustomerQuestionAnswer) {
        this.currentCustomerAnswer = answer;
        this.alertConfirmed = false;
        if (answer === CustomerQuestionAnswer.Now) {
            this.form.get("dateTime")?.setValue(new Date());
        }
    }

    public continue(): void {
        if (this.disableDefaultContinueButton) {
            return;
        }

        this.submitAnswerAndContinue();
    }

    public createFlammableAtexPermit(): void {
        this.submitAnswerAndContinue("create");
    }

    public retrieveFlammableAtexPermit(): void {
        this.submitAnswerAndContinue("retrieve");
    }

    public requestFlammableAtexPermit(): void {
        this.submitAnswerAndContinue("request");
    }

    private submitAnswerAndContinue(flammableAtexPermitAction?: "create" | "retrieve" | "request"): void {
        const answer: QuestionAnswer = {
            questionId: this.fixedQuestion?.id ?? this.customerQuestion?.id,
        };

        if (this.fixedQuestion) {
            switch (this.fixedQuestion.answerType) {
                case QuestionAnswerType.SubstanceSelection:
                    answer.selectedFlammableMaterialIds = this.getSelectedFlammableMaterialIdsFromForm();
                    break;
                case QuestionAnswerType.OptionSelection:
                    answer.selectedOptionIds = [...this.selectedQuestionOptionIds].sort((left, right) => left - right);
                    answer.textAnswer = (this.form?.get("optionSelectionFreeText")?.value as string | null)?.trim() || undefined;
                    break;
                case QuestionAnswerType.SafetyEquipmentSelection:
                    answer.safetyEquipmentAnswers = this.getSelectedSafetyEquipmentAnswers();
                    break;
                case QuestionAnswerType.SubstanceMeasurement:
                    answer.currentFlammableMaterialMeasurements = this.substanceMeasurements;
                    break;
                case QuestionAnswerType.YesNo:
                case QuestionAnswerType.YesNoNotApplicable:
                    answer.answer =
                        QuestionAnswerEnum[this.currentCustomerAnswer.toString() as keyof typeof QuestionAnswerEnum];
                    break;
                case QuestionAnswerType.YesNoWithCondition:
                    answer.answer =
                        QuestionAnswerEnum[this.currentCustomerAnswer.toString() as keyof typeof QuestionAnswerEnum];
                    answer.conditionText = this.shouldShowConditionInput
                        ? this.form.get("condition")?.value as string
                        : "";
                    break;
                case QuestionAnswerType.Text:
                    answer.textAnswer = this.form.get("textNumber")?.value as string;
                    break;
                case QuestionAnswerType.Number:
                    answer.numberAnswer = this.form.get("textNumber")?.value as number;
                    break;
                case QuestionAnswerType.DateTime:
                    answer.dateTimeAnswer = this.form.get("dateTime")?.value as Date;
                    break;
            }
        }

        if (this.customerQuestion) {
            answer.answer =
                QuestionAnswerEnum[this.currentCustomerAnswer.toString() as keyof typeof QuestionAnswerEnum];
        }

        if (flammableAtexPermitAction) {
            const permit = this.store.selectSnapshot(PermitCreateSelectors.permit);
            if (permit?.id) {
                this.actions$.pipe(
                    ofActionSuccessful(PermitQuestionActions.SaveQuestionAnswer),
                    take(1),
                    untilDestroyed(this),
                ).subscribe(() => {
                    if (flammableAtexPermitAction === "create") {
                        this.store.dispatch(
                            new PermitCreateActions.CreatePermit(
                                true,
                                permit.isDemo === true,
                                PermitType.flammableAtex,
                                permit.id,
                                permit.id,
                                true,
                            ),
                        );
                        return;
                    }

                    if (flammableAtexPermitAction === "retrieve") {
                        this.store.dispatch(
                            new Navigate([
                                PermitPaths.Root,
                                PermitPaths.RetrieveFlammableAtexPermit,
                                permit.id.toString(),
                            ], { continueFlow: true }),
                        );
                    }

                    if (flammableAtexPermitAction === "request") {
                        this.store.dispatch(
                            new Navigate([
                                PermitPaths.Root,
                                PermitPaths.RequestFlammableAtexPermit,
                                permit.id.toString(),
                            ], { continueFlow: true }),
                        );
                    }
                });

                this.store.dispatch(new PermitQuestionActions.SaveQuestionAnswer(answer));
            }

            return;
        }

        this.store.dispatch(new PermitQuestionActions.SetFixedOrCustomerQuestionAnswer(answer));
    }

    private getSelectedFlammableMaterialIdsFromForm(): number[] {
        if (!this.form) {
            return [];
        }

        return this.flammableMaterials.filter((material) =>
            this.form.get(`material-${material.flammableMaterialId}`)?.value === true
        ).map((material) => material.flammableMaterialId);
    }

    private getSelectedSafetyEquipmentAnswers(): SafetyEquipmentAnswerSelection[] {
        const selectedAnswers: SafetyEquipmentAnswerSelection[] = [...this.selectedSafetyEquipmentIds].map((
            safetyEquipmentId,
        ) => ({
            questionId: this.fixedQuestion?.id,
            safetyEquipmentId: safetyEquipmentId,
        }));

        const customText = (this.form?.get("safetyEquipmentCustomText")?.value as string | null)?.trim();
        if (this.customSafetyEquipmentSelected && customText) {
            selectedAnswers.push({
                questionId: this.fixedQuestion?.id,
                customText: customText,
            });
        }

        return selectedAnswers;
    }

    private parseOptionSelectionAnswer(rawValue: string | null | undefined): OptionSelectionAnswerValue {
        if (!rawValue?.trim()) {
            return { selectedOptionIds: [] };
        }

        try {
            const parsed = JSON.parse(rawValue) as OptionSelectionAnswerValue;
            return {
                selectedOptionIds: Array.isArray(parsed?.selectedOptionIds)
                    ? parsed.selectedOptionIds.filter((value): value is number => typeof value === "number")
                    : [],
                freeText: typeof parsed?.freeText === "string" ? parsed.freeText : null,
            };
        } catch {
            return {
                selectedOptionIds: rawValue.split(",")
                    .map((value) => Number.parseInt(value.trim(), 10))
                    .filter((value) => !Number.isNaN(value)),
            };
        }
    }

    public goBack(): void {
        this.store.dispatch(new PermitQuestionActions.GetPreviousQuestion());
    }

    public getCheckpointAlternativeNumbers(customerQuestionAnswer: CustomerQuestionAnswer): number[] {
        const choiceNumbers: number[] = [];
        let i = 0;
        let cqa: number;

        while (CustomerQuestionAnswer[cqa = 1 << i++]) {
            if (customerQuestionAnswer & cqa) {
                choiceNumbers.push(cqa);
            }
        }

        return choiceNumbers;
    }

    public showUpdateTimeModal(): void {
        const now = new Date();
        this.modalService.open(WheelDatetimePickerModalComponent, true, {
            default: now.toISOString(),
            maxValue: 0,
            minValue: -(3 * 24),
        });
    }

    public getCommentTranslation(translations: Translation[] | null | undefined): string | null {
        if (!translations?.length) {
            return null;
        }

        const currentLanguage = this.store.selectSnapshot<string>((_: { settings: { currentLanguage: string; }; }) =>
            _.settings.currentLanguage
        );

        return translations.find((translation) => translation.language === currentLanguage)?.text ?? null;
    }

    public get missingCommentTranslationText(): string {
        return PermitQuestionComponent.missingCommentTranslationText;
    }

    private getQuestionKey(): string | null {
        if (this.fixedQuestion) {
            return `fixed-${this.fixedQuestion.id}`;
        }

        if (this.customerQuestion) {
            return `customer-${this.customerQuestion.id}`;
        }

        return null;
    }

    private hasValidCustomerAnswer(answer: CustomerQuestionAnswer | null | undefined): answer is CustomerQuestionAnswer {
        return typeof answer === "number" && answer > 0;
    }
}
